import React, { useState } from 'react';
import { generateQuiz } from '../lib/anthropic';
import toast, { Toaster } from 'react-hot-toast';

const subjects = ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'English', 'History', 'Geography', 'Computer Science'];
const years = ['Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11', 'Year 12', 'Year 13'];
const difficulties = [
  { value: 'easy', label: 'Easy' },
  { value: 'medium', label: 'Medium' },
  { value: 'hard', label: 'Hard' }
];
const languages = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ru', name: 'Russian' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'ar', name: 'Arabic' },
  { code: 'hi', name: 'Hindi' }
];

export default function AiQuiz() {
  const [subject, setSubject] = useState('');
  const [year, setYear] = useState('');
  const [topic, setTopic] = useState('');
  const [difficulty, setDifficulty] = useState('medium');
  const [numberOfQuestions, setNumberOfQuestions] = useState(5);
  const [language, setLanguage] = useState('en');
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [quizStarted, setQuizStarted] = useState(false);

  const handleStartQuiz = async (e) => {
    e.preventDefault();
    setLoading(true);
    setQuizResult(null);
    setUserAnswers({});

    try {
      const generatedQuestions = await generateQuiz({
        subject,
        year,
        topic,
        difficulty,
        number_of_questions: parseInt(numberOfQuestions), 
        language
      });
      setQuestions(generatedQuestions);
      setCurrentQuestionIndex(0);
      setQuizStarted(true);
    } catch (error) {
      toast.error(error.message || 'Failed to generate quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, answer) => {
    setUserAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      calculateResults();
    }
  };

  const calculateResults = () => {
    const results = questions.map(question => ({
      question,
      userAnswer: userAnswers[question.id] || '',
      isCorrect: userAnswers[question.id] === question.correctAnswer
    }));

    const correctAnswers = results.filter(r => r.isCorrect).length;
    const score = (correctAnswers / questions.length) * 100;

    let feedback = '';
    if (score >= 90) feedback = 'Outstanding! You have an excellent understanding of the subject.';
    else if (score >= 70) feedback = 'Good job! You show strong knowledge but there\'s room for improvement.';
    else if (score >= 50) feedback = 'You\'re on the right track, but some topics need more practice.';
    else feedback = 'Keep practicing! Review the topics you found challenging.';

    setQuizResult({
      totalQuestions: questions.length,
      correctAnswers,
      incorrectAnswers: questions.length - correctAnswers,
      score,
      feedback,
      questionResults: results
    });
  };

  const resetQuiz = () => {
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setQuizResult(null);
    setQuizStarted(false);
  };

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div className="container py-5">
      <Toaster position="top-right" />
      <h2 className="text-center mb-4">🎓 AI-Powered Quiz</h2>

      {!quizStarted ? (
        <form className="row g-4" onSubmit={handleStartQuiz}>
          <div className="col-md-6">
            <label className="form-label">Subject</label>
            <select className="form-select" value={subject} onChange={e => setSubject(e.target.value)} required>
              <option value="">Select Subject</option>
              {subjects.map(sub => <option key={sub} value={sub}>{sub}</option>)}
            </select>
          </div>

          <div className="col-md-6">
            <label className="form-label">Year</label>
            <select className="form-select" value={year} onChange={e => setYear(e.target.value)} required>
              <option value="">Select Year</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          <div className="col-md-6">
            <label className="form-label">Topic</label>
            <input type="text" className="form-control" value={topic} onChange={e => setTopic(e.target.value)} required />
          </div>

          <div className="col-md-6">
            <label className="form-label">Difficulty</label>
            <select className="form-select" value={difficulty} onChange={e => setDifficulty(e.target.value)}>
              {difficulties.map(diff => (
                <option key={diff.value} value={diff.value}>{diff.label}</option>
              ))}
            </select>
          </div>

          <div className="col-md-6">
            <label className="form-label">Number of Questions</label>
            <input type="number" className="form-control" value={numberOfQuestions} onChange={e => setNumberOfQuestions(e.target.value)} min={1} max={20} required />
          </div>

          <div className="col-md-6">
            <label className="form-label">Language</label>
            <select className="form-select" value={language} onChange={e => setLanguage(e.target.value)}>
              {languages.map(lang => (
                <option key={lang.code} value={lang.code}>{lang.name}</option>
              ))}
            </select>
          </div>

          <div className="col-12 mt-2">
            <button className="btn btn-primary w-100" type="submit" disabled={loading}>
              {loading ? 'Generating Quiz...' : 'Start Quiz'}
            </button>
          </div>
        </form>
      ) : (
        <div className="card shadow-sm">
          <div className="card-body">
            {quizResult ? (
              <>
                <h4 className="mb-3">Quiz Results</h4>
                <p><strong>Score:</strong> {quizResult.score.toFixed(2)}%</p>
                <p><strong>Correct Answers:</strong> {quizResult.correctAnswers}</p>
                <p><strong>Incorrect Answers:</strong> {quizResult.incorrectAnswers}</p>
                <p className="fw-bold text-success">{quizResult.feedback}</p>
                <button className="btn btn-secondary mt-3" onClick={resetQuiz}>Try Again</button>
              </>
            ) : (
              <>
                <h5 className="mb-3">Question {currentQuestionIndex + 1} of {questions.length}</h5>
                <p className="mb-3">{currentQuestion?.question}</p>
                <div className="list-group mb-3">
                  {currentQuestion?.options.map(option => (
                    <button
                      key={option}
                      type="button"
                      className={`list-group-item list-group-item-action ${userAnswers[currentQuestion.id] === option ? 'active' : ''}`}
                      onClick={() => handleAnswer(currentQuestion.id, option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
                <button className="btn btn-success w-100" onClick={handleNextQuestion}>
                  {currentQuestionIndex === questions.length - 1 ? 'Finish Quiz' : 'Next'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
